import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import FormContainer from "../../containers/FormContainer";
import TextBox from "../shared/TextBox";
import SubmitButton from "../shared/SubmitButton";
import { useModal } from "../modal/ModalContext";
import {
  challengeLinkedInAccount,
  requestLinkedInEmailOtp,
} from "../../services/apiService";
import { getErrorMessage, notifyError } from "../../helpers/apiError";

/**
 * HeyReach-style PIN entry for LinkedIn challenge / email OTP.
 */
const ChallengeModal = ({
  accountId,
  onSuccess,
  challengeType = "EMAIL_PIN",
  suggestedAction = "submit_challenge",
}) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestingEmail, setIsRequestingEmail] = useState(false);
  const [formError, setFormError] = useState(null);
  const [code, setCode] = useState("");
  const [activeChallengeType, setActiveChallengeType] = useState(challengeType);
  const [activeSuggestedAction, setActiveSuggestedAction] = useState(suggestedAction);
  const needsEmailOtp =
    activeSuggestedAction === "request_email_otp" || activeChallengeType === "APP";

  const handleCodeChange = (e) => {
    const next = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(next);
  };

  const handleRequestEmailOtp = async () => {
    setIsRequestingEmail(true);
    setFormError(null);
    try {
      const response = await requestLinkedInEmailOtp(accountId);
      const body = response?.data?.data || response?.data || {};
      if (body.success || body.challengeType === "EMAIL_PIN") {
        setActiveChallengeType("EMAIL_PIN");
        setActiveSuggestedAction("submit_challenge");
        toast.success(t("linkedin.toast.emailOtpSent"));
      } else {
        const msg = body.message || body.error?.message || t("linkedin.toast.error");
        setFormError(msg);
        toast.error(typeof msg === "string" ? msg : t("linkedin.toast.error"));
      }
    } catch (err) {
      const msg = getErrorMessage(err, t("linkedin.toast.error"));
      setFormError(msg);
      notifyError(err, t("linkedin.toast.error"));
    } finally {
      setIsRequestingEmail(false);
    }
  };

  const handleSubmit = async () => {
    if (code.length < 4) return;
    setIsLoading(true);
    setFormError(null);
    try {
      const response = await challengeLinkedInAccount(accountId, { code });
      const body = response?.data?.data || response?.data || {};
      if (body.success === false) {
        const msg = body.message || t("linkedin.toast.invalidPin");
        setFormError(msg);
        toast.error(msg);
        return;
      }
      toast.success(t("linkedin.toast.connectSuccess"));
      hideModal();
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = getErrorMessage(err, t("linkedin.toast.error"));
      setFormError(msg);
      notifyError(err, t("linkedin.toast.error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-[40px]">
      <p className="text-gray-500 text-sm mb-2">{t("linkedin.pinSubhead")}</p>
      <ul className="text-gray-500 text-xs mb-5 list-disc ps-5 space-y-1">
        <li>{t("linkedin.pinHintEmail")}</li>
        <li>{t("linkedin.pinHintSms")}</li>
        <li>{t("linkedin.pinHintApp")}</li>
      </ul>
      {needsEmailOtp && (
        <div className="mb-4 p-3 rounded-md bg-purple-50 border border-purple-100">
          <p className="text-sm text-purple-800 mb-2">{t("linkedin.appChallengeHint")}</p>
          <button
            type="button"
            onClick={handleRequestEmailOtp}
            disabled={isRequestingEmail}
            className="text-sm font-bold text-taction hover:underline disabled:opacity-50"
          >
            {isRequestingEmail ? t("linkedin.requestingEmailCode") : t("linkedin.requestEmailCode")}
          </button>
        </div>
      )}
      <FormContainer isLoading={isLoading} error={formError} onSubmit={handleSubmit}>
        <TextBox
          label={t("linkedin.pinLabel")}
          name="code"
          value={code}
          onChange={handleCodeChange}
          placeholder={t("linkedin.pinPlaceholder")}
          inputMode="numeric"
          autoComplete="one-time-code"
        />
        <div className="flex items-center gap-3 mt-2">
          <SubmitButton
            customStyle="inline"
            label={t("linkedin.verifyPin")}
            disabled={code.length < 4}
            isLoading={isLoading}
          />
          <button
            type="button"
            onClick={hideModal}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium text-sm"
          >
            {t("linkedin.close")}
          </button>
        </div>
      </FormContainer>
    </div>
  );
};

export default ChallengeModal;
